import { IDeliveryWindowEntities } from "records/item";
import { IShipDate } from "records/order";
import moment, { MomentInput } from "moment";
import { DeliveryWindow } from "records/item/delivery-window";
import { FUTURE, IMMEDIATE } from "constants/delivery-window-types";
import _ from "lodash";

export const DATE_FORMAT = "MM/DD/YY";
export const SHIPMENT_DATE_FORMAT = "YYYY/MM/DD";
export const HEADER_DATE_FORMAT = "MMM DD YYYY";
export const MOMENT_INCLUSION = "[]";
export const GRANULARITY = "day";

export const getValidDeliveryWindowEntities = (ids: string[] = [], entities: IDeliveryWindowEntities) =>
    _.reduce(ids, (map, id) => (entities[id] ? { ...map, [id]: entities[id] } : map), {} as IDeliveryWindowEntities);

export const validateShipDates = ({ cart, deliveryWindowEntities }) => {
    const ship_dates = getOrder(cart)?.__build_order?.ship_dates ?? [];
    return getBuildOrderDeliveryWindows({ ship_dates, deliveryWindowEntities });
};

export const getDisplayName = ({ ship_start, ship_end }) => {
    const from = moment(ship_start, SHIPMENT_DATE_FORMAT).format(HEADER_DATE_FORMAT);
    const to = moment(ship_end, SHIPMENT_DATE_FORMAT).format(HEADER_DATE_FORMAT);
    return `${from} - ${to}`;
};

const getBuildOrderDeliveryWindows = ({ ship_dates, deliveryWindowEntities }) => {
    if (!deliveryWindowEntities) {
        return [];
    }

    const newDeliveryWindows = [];
    const deliveryWindows: DeliveryWindow[] = Object.values(deliveryWindowEntities);
    const [mainWindow] = deliveryWindows;
    const latestDate = mainWindow?.latest_available_date ?? moment.max(ship_dates.map((d) => moment.utc(d.ship_end)));

    ship_dates.forEach((shipDate) => {
        // build order assumes brand has one default adjustable delivery window
        // ! In case there are more DWs in the brand, we can create multiple build order DWs for the same date !
        deliveryWindows.forEach((dw) => {
            if (isDateInWindowRange({ targetDate: shipDate.ship_start, dw, latestDate }) && dw.has("start")) {
                const newStart = toMomentUtc(shipDate.ship_start);
                const newEnd = toMomentUtc(shipDate.ship_end);
                let newType = dw.get("type");
                if (newType === IMMEDIATE && newStart.isAfter(moment.utc(), GRANULARITY)) {
                    newType = FUTURE;
                }
                newDeliveryWindows.push(
                    dw
                        .set("_id", `${shipDate._id}-${newType}`)
                        .set("type", newType)
                        .set("start", newStart)
                        .set("end", newEnd)
                        .set("display_name", getDisplayName(shipDate))
                        .set("latest_available_date", newEnd),
                );
            }
        });
    });
    return newDeliveryWindows;
};

export const toMomentUtc = (date: MomentInput, format = SHIPMENT_DATE_FORMAT) => moment.utc(date, format);

const isDateInWindowRange = ({ targetDate, dw, latestDate }) =>
    toMomentUtc(targetDate).isBetween(dw.earliest_available_date, latestDate, GRANULARITY, MOMENT_INCLUSION);

export const SNACK_ID = "build-order-shipment";

export { hasBuildOrderEnabled } from "../../hooks/useBuildOrder";

export const getOrder = (cart) => cart?.entities?.orders?.[cart?.result];

export const getHasShipDates = ({ order }) => {
    if (order) {
        const { ship_dates = [] } = order.__build_order;
        return ship_dates.length !== 0;
    }
    return false;
};

export const getShouldShowCartAction = ({ shouldShowBuildOrder, order }) => {
    if (shouldShowBuildOrder) {
        const hasShipDates = getHasShipDates({ order });
        return hasShipDates ? hasShipDates : order.line_items_count > 0;
    }
    return true;
};

export const getBOShipDates = ({ buildOrderShipDates }) =>
    buildOrderShipDates
        .map((shipDate) => ({
            ship_start: moment.utc(shipDate.ship_start, SHIPMENT_DATE_FORMAT).valueOf(),
            ship_end: moment.utc(shipDate.ship_end, SHIPMENT_DATE_FORMAT).valueOf(),
        }))
        .sort((a, b) => a.ship_start - b.ship_start);

export const checkDuplicateDate = (dates: IShipDate[], index: number) =>
    !!dates
        .slice(0, index)
        .find(
            (shipDate) =>
                moment(shipDate?.ship_start).format("YYYY-MM-DD") ===
                moment(dates[index]?.ship_start).format("YYYY-MM-DD"),
        );

export const formatDateKey = (shipStart, shipEnd) =>
    `${moment.utc(shipStart).format(SHIPMENT_DATE_FORMAT)}-${moment.utc(shipEnd).format(SHIPMENT_DATE_FORMAT)}`;

export const buildOrderDictionary = (shipDates: IShipDate[]) => {
    return shipDates.reduce((accumulator, shipDate) => {
        const key = formatDateKey(shipDate.ship_start, shipDate.ship_end);
        accumulator[key] = shipDate.line_items || [];
        return accumulator;
    }, {});
};
